<template>
  <DialogWrapper
    class="location-map-dialog"
    width="40%"
    hideFooter
    hiddenHeaderLine
    :show.sync="showDialog"
    @closed="handleClosed"
  >
    <div v-loading="loading" class="location-container">
      <div class="map-container" :id="map.container"></div>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { getAMap } from '@/plugins/amap'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  components: {
    DialogWrapper
  },
  props: {
    locationList: {
      type: Array,
      default: () => []
    },
    defaultLocation: {
      required: false,
      type: Object,
      default: () => {}
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.job.editor',
      loading: false,
      mapLoader: null,
      map: {
        container: 'mapContainer',
        hasInit: false,
        instance: null,
        geoCoder: null,
        toolbar: null,
        markerList: [],
        viewMode: '2D'
      }
    }
  },
  computed: {
    showDialog: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    }
  },
  created () {
  },
  methods: {
    async renderMap () {
      if (!this.map.instance || !this.map.hasInit) {
        await this.initMap()
      }
      await this.initLocations()
    },
    handleClosed () {
      this.map.instance = null
      this.map.hasInit = false
      this.map.markerList = []
    },
    async loadAMap () {
      if (!this.mapLoader) {
        const plugins = ['AMap.Geocoder', 'AMap.PlaceSearch', 'AMap.ToolBar']
        this.mapLoader = getAMap(plugins)
      }
      return this.mapLoader
    },
    async initMap () {
      this.loading = true
      const AMap = await this.loadAMap()
      const markerIcon = new AMap.Icon({
        image: this.$DjangoStaticUrl('/image/wechat/marker.png'),
        imageSize: new AMap.Size(25, 34)
      })
      const mapInstance = new AMap.Map(this.map.container, {
        zoom: 13,
        viewMode: this.map.viewMode
      })
      this.map.toolbar = new AMap.ToolBar({
        position: {
          top: '14px',
          right: '14px'
        }
      })
      mapInstance.addControl(this.map.toolbar)
      this.map.instance = mapInstance
      this.map.markerIcon = markerIcon
      this.map.hasInit = true
      this.loading = false
    },
    async initLocations () {
      const locationPoints = this.getPoints(this.locationList)
      if (locationPoints?.length) {
        const AMap = await this.loadAMap()
        const lngLat = new AMap.LngLat(locationPoints[0].lng, locationPoints[0].lat)
        const address = locationPoints[0].address
        this.map.instance.lang = this.$i18n.locale
        this.map.instance.center = lngLat
        if (locationPoints.length > 1) {
          _.forEach(locationPoints, (item, index) => {
            const lngLat = new AMap.LngLat(item.lng, item.lat)
            this.addMapMarkerPosition(lngLat, item.address, index + 1)
          })
        } else {
          await this.addMapMarkerPosition(lngLat, address)
        }
        if (this.defaultLocation) {
          const lng = parseFloat(this.defaultLocation.lng)
          const lat = parseFloat(this.defaultLocation.lat)
          if (lng && lat) {
            this.setMapMarkerPosition([lng, lat])
          }
        }
      }
    },
    async addMapMarkerPosition (lngLat, address, index) {
      this.loading = true
      const AMap = await this.loadAMap()
      const map = this.map
      const mapInstance = map.instance
      const marker = new AMap.Marker({
        icon: map.markerIcon,
        position: lngLat,
        offset: new AMap.Pixel(-8, 6),
        anchor: 'bottom-center',
        title: address,
        label: {
          offset: new AMap.Pixel(0, -34),
          content: `<div class="addressLabel">${address}</div>`
        }
      })
      const markerTemplate = `<div class="marker-icon">
                              <i class="iconfont iconAPP-location"></i>
                            </div>`
      marker.setContent(markerTemplate)
      marker.on('click', () => {
        mapInstance.setZoomAndCenter(18, lngLat)
      })

      if (!marker.getMap()) {
        marker.setMap(mapInstance)
      }
      map.markerList.push(marker)
      if (!index || index === this.locationList.length) {
        mapInstance.setFitView()
      }

      this.loading = false
    },
    async setMapMarkerPosition (lngLat) {
      const map = this.map
      const mapInstance = map.instance
      mapInstance.setCenter(lngLat)
    },
    getPoints (locationList) {
      const locationPoints = []
      _.forEach(locationList, location => {
        const lng = location.lng
        const lat = location.lat
        const address = location.address
        const city = location.city
        const region = location.region
        const cbd = location.cbd
        const addressList = []
        if (city.id) {
          addressList.push(city.i18nName)
        }
        if (region.id) {
          addressList.push(region.i18nName)
        }
        if (cbd.id) {
          addressList.push(cbd.i18nName)
        }
        if (address) {
          addressList.push(address)
        }
        if (lng && lat) {
          locationPoints.push({
            lng: parseFloat(lng),
            lat: parseFloat(lat),
            address: addressList.join(',')
          })
        }
      })
      return locationPoints
    }
  },
  watch: {
    showDialog () {
      if (this.showDialog) {
        this.renderMap()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.location-map-dialog {
  .location-container {

    .map-container {
      margin-top: 6px;
      height: 500px;

      ::v-deep {
        .marker-icon {
          position: relative;
          width: 24px;
          height: 34px;
          text-align: center;
          .iconfont {
            font-size: 40px;
            color: var(--custom-color);
          }
          .job-count-marker {
            left: 52%;
            top: 34%;
            position: absolute;
            display: inline-block;
            width: 100%;
            height: 100%;
            background: var(--custom-color);;
            color: #FFF;
            font-size: 16px;
          }
        }

        .amap-marker-label {
          border: none;
          transform: translateX(-50%);
        }
      }

    }
  }
}
</style>
